import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Application } from '@/types/startup';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from './use-toast';

export const useStartupApplications = (startups: any[] | undefined) => {
  const [applications, setApplications] = useState<Application[]>([]);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  useEffect(() => {
    if (!startups?.length) return;

    const fetchApplications = async () => {
      const startupIds = startups.map(s => s.id);
      console.log('Fetching applications for startups:', startupIds);
      
      const { data, error } = await supabase
        .from('startup_applications')
        .select(`
          *,
          profiles (full_name, avatar_url),
          startup_roles (title),
          startups (name)
        `)
        .in('startup_id', startupIds)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching applications:', error);
        return;
      }

      console.log('Fetched applications:', data);
      setApplications(data || []);
    };

    fetchApplications();

    // Subscribe to changes in startup_applications
    const channel = supabase
      .channel('startup_applications_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'startup_applications',
          filter: `startup_id=in.(${startups.map(s => s.id).join(',')})`,
        },
        async (payload) => {
          console.log('Received real-time update:', payload);
          await fetchApplications();
        }
      )
      .subscribe();

    return () => {
      console.log('Cleaning up subscription');
      supabase.removeChannel(channel);
    };
  }, [startups]);

  const handleStatusUpdate = async (applicationId: string, status: string) => {
    console.log('Handling status update:', { applicationId, status });
    
    try {
      const { error } = await supabase
        .from('startup_applications')
        .update({ status })
        .eq('id', applicationId);

      if (error) {
        console.error('Error updating application status:', error);
        toast({
          title: "Error",
          description: "Failed to update application status",
          variant: "destructive",
        });
        return;
      }

      // Update local state immediately
      setApplications(prev =>
        prev.map(app =>
          app.id === applicationId ? { ...app, status } : app
        )
      );

      // Refresh startups data to get updated team members
      queryClient.invalidateQueries({ queryKey: ['user-startups'] });

      toast({
        title: "Success",
        description: `Application ${status}`,
      });
    } catch (error) {
      console.error('Error in handleStatusUpdate:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    }
  };

  return { applications, handleStatusUpdate };
};